import { computed } from 'vue';

export default function useGridTile({ tileSizes, productsCount, productGridColCount }) {
  const displayTiles = computed(() => {
    return {
      ratio320px: validateTile('ratio320px'),
      ratio768px: validateTile('ratio768px'),
      ratio1200px: validateTile('ratio1200px'),
      ratio1400px: validateTile('ratio1400px'),
      ratio1800px: validateTile('ratio1800px'),
    };
  });

  // Count the number of grid cells that all the promo tiles are gonna occupy
  const tileGridCellCount = computed(() => {
    return {
      ratio320px: getCellCount('ratio768px'), // use 768 here because ProductGridTile CSS for 320 uses ratio 768
      ratio768px: getCellCount('ratio1200px'), // use 1200 here because ProductGridTile CSS for 768 uses ratio 1200
      ratio1200px: getCellCount('ratio1200px'),
      ratio1400px: getCellCount('ratio1400px'),
      ratio1800px: getCellCount('ratio1800px'),
    };
  });

  /**
   * The `validators` function handles different validation scenarios against the product tile ratio value
   *
   * @returns {object} collection of validation functions
   */
  const validators = () => {
    const gridAreaFormatRegex = new RegExp('[0-9]{1,2}/[1-5]/[0-9]{1,2}/[1-5]');

    return {
      isValidFormatGridArea: (value) => gridAreaFormatRegex.test(value),
      isThereEnoughGridColToShowTile: (tileColEnd) => {
        switch (tileColEnd) {
          case 1:
            return productGridColCount.value >= 1;
          case 2:
            return productGridColCount.value >= 1;
          case 3:
            return productGridColCount.value >= 2;
          case 4:
            return productGridColCount.value >= 3;
          case 5:
            return productGridColCount.value === 4;
          default:
            return false;
        }
      },

      isThereEnoughGridRowToShowTile: (tileRowEnd) => productsCount.value / productGridColCount.value >= tileRowEnd,
    };
  };

  const getTilePositionsByRatio = (tileRatio, tileSizes) => {
    return tileSizes[tileRatio].split('/').map((item) => Number(item.trim()));
  };

  /**
   * The `validateTile` function determine if the tile should show or not
   * @param {string} tileRatio e.g ("2/2/2/2")
   * @returns {string} block or none
   */
  const validateTile = (tileRatio) => {
    if (tileRatio in tileSizes && tileSizes[tileRatio]) {
      const [tileRowStart, tileColStart, tileRowEnd, tileColEnd] = getTilePositionsByRatio(tileRatio, tileSizes);
      const { isValidFormatGridArea, isThereEnoughGridColToShowTile, isThereEnoughGridRowToShowTile } = validators();

      if (
        tileRowStart < tileRowEnd &&
        tileColStart < tileColEnd &&
        isValidFormatGridArea(tileSizes[tileRatio]) &&
        isThereEnoughGridColToShowTile(tileColEnd) &&
        isThereEnoughGridRowToShowTile(tileRowEnd)
      ) {
        return 'block';
      } else {
        return 'none';
      }
    } else {
      return 'none';
    }
  };

  /**
   * The `getCellCount` function will calculate the number of grid cells per screen ratio that the grid area will occupy
   * @param {*} tileRatio
   * @returns
   */
  const getCellCount = (tileRatio) => {
    if (tileRatio in tileSizes && tileSizes[tileRatio]) {
      const [tileRowStart, tileColStart, tileRowEnd, tileColEnd] = getTilePositionsByRatio(tileRatio, tileSizes);
      const rows = Math.abs(tileRowStart - tileRowEnd);
      const cols = Math.abs(tileColStart - tileColEnd);
      return rows * cols;
    }
    return 0;
  };

  return {
    displayTiles,
    tileGridCellCount,
    validators,
    validateTile,
  };
}
